import React, { useState, useEffect, useRef } from "react";

import { ref, push } from "firebase/database";
import { db } from "../services/firebase";
import { useNavigate } from "react-router-dom";
import {
  FaUniversity,
  FaCheck,
  FaTimes,
  FaCircle,
  FaCode,
  FaChartBar,
  FaClock,
  FaTrophy,
  FaCheckCircle,
  FaExclamationTriangle,
  FaQuestionCircle
} from "react-icons/fa";
import "./CodeSnippetResult.css";
import { Target } from "lucide-react";

function CodeSnippetResult({ user, quizData, answers, onBackToTest, timeLeft, isFromTest = false }) {
  const [score, setScore] = useState(null);
  const [resultSaved, setResultSaved] = useState(false);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasCalculatedRef = useRef(false);

  const navigate = useNavigate();

  // useEffect(() => {
  //   setLoading(true);
  //   setError(null);
    
  //   if (quizData && answers && !isFromTest) {
  //     try {
  //       calculateAndSaveResult();
  //     } catch (err) {
  //       setError('Error calculating results: ' + err.message);
  //     }
  //   } else if (quizData && answers && isFromTest) {
  //     // If coming from test, calculate and save automatically
  //     try {
  //       calculateAndSaveResult();
  //     } catch (err) {
  //       setError('Error calculating results: ' + err.message);
  //     }
  //   } else {
  //     const stored = localStorage.getItem("latestCodeSnippetResult");
  //     if (stored) {
  //       try {
  //         const parsedResult = JSON.parse(stored);
          
  //         // Normalize the score data to ensure consistent structure
  //         const normalizedScore = {
  //           correct: parsedResult.correctAnswers || parsedResult.correct || 0,
  //           total: parsedResult.totalQuestions || parsedResult.total || 0,
  //           totalMarks: parsedResult.maxMarks || parsedResult.totalMarks || 0,
  //           obtainedMarks: parsedResult.score || parsedResult.obtainedMarks || 0,
  //           percentage: parsedResult.accuracy ? parseFloat(parsedResult.accuracy) : 0,
  //           quizTitle: parsedResult.quizTitle || 'Code Snippet Quiz',
  //           topic: parsedResult.topic || 'Code Snippet',
  //           answers: parsedResult.answers || [],
  //           questions: parsedResult.questions || [],
  //           submittedAt: parsedResult.submittedAt || new Date().toISOString(),
  //           timeTaken: parsedResult.timeTaken || '00:00'
  //         };
          
  //         setScore(normalizedScore);
  //         setResultSaved(true);
  //       } catch (err) {
  //         setError('Error loading saved result: ' + err.message);
  //       }
  //     } else {
  //       setError('No result data found');
  //     }
  //   }
  //   setLoading(false);
  // }, [quizData, answers, isFromTest]);

  useEffect(() => {
  setLoading(true);
  setError(null);

  if (!hasCalculatedRef.current && quizData && answers) {
    try {
      calculateAndSaveResult();
      hasCalculatedRef.current = true;
    } catch (err) {
      setError('Error calculating results: ' + err.message);
    } finally {
      setLoading(false);
    }
  } else if (!quizData || !answers) {
    const stored = localStorage.getItem("latestCodeSnippetResult");
    if (stored) {
      try {
        const parsedResult = JSON.parse(stored);

        const normalizedScore = {
          correct: parsedResult.correctAnswers || parsedResult.correct || 0,
          total: parsedResult.totalQuestions || parsedResult.total || 0,
          totalMarks: parsedResult.maxMarks || parsedResult.totalMarks || 0,
          obtainedMarks: parsedResult.score || parsedResult.obtainedMarks || 0,
          percentage: parsedResult.accuracy ? parseFloat(parsedResult.accuracy) : 0,
          quizTitle: parsedResult.quizTitle || 'Code Snippet Quiz',
          topic: parsedResult.topic || 'Code Snippet',
          answers: parsedResult.answers || [],
          questions: parsedResult.questions || [],
          submittedAt: parsedResult.submittedAt || new Date().toISOString(),
          timeTaken: parsedResult.timeTaken || '00:00'
        };

        setScore(normalizedScore);
        setResultSaved(true);
      } catch (err) {
        setError('Error loading saved result: ' + err.message);
      } finally {
        setLoading(false);
      }
    } else {
      setError('No result data found');
      setLoading(false);
    }
  } else {
    setLoading(false); // fallback if nothing triggers above
  }
}, [quizData, answers]);

  
  
  const calculateAndSaveResult = () => {
    if (!quizData || !quizData.questions || !answers) {
      console.error('Missing required data for result calculation');
      return;
    }

    console.log('Calculating results...');
    console.log('Quiz data:', quizData);
    console.log('Answers:', answers);

    const questions = quizData.questions || [];
    let correct = 0;
    let totalMarks = 0;
    let obtainedMarks = 0;

    questions.forEach((question, index) => {
      const userAnswer = answers[index];
      const correctAnswer = question.correctAnswer;
      const questionMarks = question.marks || 1;
      
      totalMarks += questionMarks;
      
      console.log(`Question ${index + 1}: User answer: ${userAnswer}, Correct: ${correctAnswer}, Marks: ${questionMarks}`);
      
      if (userAnswer && userAnswer === correctAnswer) {
        correct++;
        obtainedMarks += questionMarks;
        console.log(`Question ${index + 1}: CORRECT!`);
      } else {
        console.log(`Question ${index + 1}: WRONG or UNANSWERED`);
      }
    });

    const percentage = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;
    
    const resultData = {
      correct,
      total: questions.length,
      totalMarks,
      obtainedMarks,
      percentage,
      answers,
      quizId: quizData.id,
      quizTitle: quizData.title,
      submittedAt: new Date().toISOString(),
      userId: user?.uid || 'anonymous',
      userEmail: user?.email || 'anonymous'
    };

    console.log('Calculated result data:', resultData);
    setScore(resultData);
    saveResultToFirebase(resultData);
  };

  const saveResultToFirebase = async (resultData) => {
    try {
      if (!quizData || !quizData.questions || !answers) {
        console.error('Missing required data for Firebase save');
        return;
      }

      if (!user || !user.email) {
        console.error('User not authenticated');
        setError('User not authenticated. Please log in again.');
        return;
      }

      console.log('Starting Firebase save for code snippet result...');
      console.log('User:', user.email);
      console.log('Quiz data:', quizData);

      // Ensure we have a valid quizId
      const quizId = quizData.id || quizData.title || `codeSnippet-${Date.now()}`;
      const quizTitle = quizData.title || 'Code Snippet Quiz';

      // Format answers to match MCQ/Coding structure exactly
      const formattedAnswers = quizData.questions.map((question, index) => ({
        qid: question.id || index + 1,
        question: question.description || question.title || `Question ${index + 1}`,
        selected: answers[index] || null,
        correct: question.correctAnswer,
        explanation: question.explanation || ""
      }));
      
      // Create result data in the exact same format as MCQ
      const submissionDetails = {
        user: user.email,
        quizId: quizId,
        testType: "codeSnippet",
        topic: "Code Snippet",
        quizTitle: quizTitle,
        score: resultData.obtainedMarks || 0,
        accuracy: (resultData.percentage || 0).toFixed(2),
        timeTaken: timeLeft ? `${Math.floor((quizData.countdown - timeLeft) / 60)}:${String((quizData.countdown - timeLeft) % 60).padStart(2, '0')}` : '00:00',
        submittedAt: new Date().toISOString(),
        source: "Dashboard",
        testId: `codeSnippet-${quizTitle}-${Date.now()}`,
        answers: formattedAnswers,
        // Additional code snippet specific data
        questions: quizData.questions,
        totalQuestions: resultData.total || 0,
        correctAnswers: resultData.correct || 0,
        maxMarks: resultData.totalMarks || 0
      };
      
      // Validate all required fields before saving
      if (!submissionDetails.user || !submissionDetails.quizId || !submissionDetails.quizTitle) {
        console.error('Missing required fields:', submissionDetails);
        setError('Missing required data for saving result');
        return;
      }
      
      console.log('Submission details:', submissionDetails);
      
      // Use the exact same pattern as TestMCQ
      const result = await push(ref(db, "results"), submissionDetails);
      console.log('Firebase save successful:', result.key);
      
      // Save to localStorage like other result components
      localStorage.setItem("latestCodeSnippetResult", JSON.stringify(submissionDetails));
      
      setResultSaved(true);
      
      // Only navigate if not from test (for independent viewing)
      if (!isFromTest) {
        setTimeout(() => {
          navigate('/view-results');
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving result:', error);
      setError('Error saving result: ' + error.message);
    }
  };

  const handleQuestionClick = (index) => {
    setSelectedQuestionIndex(index);
  };

  const getQuestionStatus = (index) => {
    const userAnswer = currentAnswers[index];
    const correctAnswer = questions[index]?.correctAnswer || 
                         (score?.answers && score.answers[index]?.correct);
    
    // For result view - show correct/wrong/unanswered
    if (!userAnswer) return 'unanswered';
    return userAnswer === correctAnswer ? 'correct' : 'wrong';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct':
        return <FaCheckCircle className="status-icon correct" />;
      case 'wrong':
        return <FaExclamationTriangle className="status-icon wrong" />;
      case 'unanswered':
        return <FaQuestionCircle className="status-icon unanswered" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'correct':
        return 'Correct Answered';
      case 'wrong':
        return 'Wrong Answered';
      case 'unanswered':
        return 'Unanswered';
      default:
        return '';
    }
  };

  if (loading) {
    return <div className="loading">Loading results...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  if (!score) {
    return <div className="loading">Calculating results...</div>;
  }

  // Use quizData if available, otherwise use score data
  const questions = quizData?.questions || score?.questions || [];
  const currentAnswers = answers || (score?.answers ? 
    score.answers.reduce((acc, answer) => {
      acc[answer.qid - 1] = answer.selected;
      return acc;
    }, {}) : {});

  return (
    <div className="codeSnippetResult-container">
      {/* Header */}
      <div className="codeSnippetResult-header">
        <div className="codeSnippetResult-header-left">
          <FaUniversity className="codeSnippetResult-university-logo" />
          <div className="codeSnippetResult-university-info">
            <h2>BodhAI</h2>
            <p>Code Snippet Assessment Results</p>
          </div>
        </div>
        <div className="codeSnippetResult-header-right">
          <div className="codeSnippetResult-marks-info">
            <span>Total Max Marks: {score?.totalMarks || 0}</span>
            <span>Total Marks Obtained: {score?.obtainedMarks || 0}</span>
          </div>
          {isFromTest ? (
            <button 
              className="codeSnippetResult-back-btn"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
          ) : (
            <button 
              className="codeSnippetResult-view-all-btn"
              onClick={() => navigate('/dashboard', { state: { target: '_blank' } })}
            >
              View All Results
            </button>
          )}
        </div>
      </div>

      <div className="codeSnippetResult-content">
        {/* Sidebar for Results */}
        <div className="codeSnippetResult-sidebar">
          <div className="codeSnippetResult-sidebar-header">
            <span>{user?.displayName || user?.email || 'User'}</span>
            <span>{score?.correct || 0} of {score?.total || 0} questions answered correctly</span>
          </div>
          
          <div className="codeSnippetResult-question-navigation">
            <div className="codeSnippetResult-nav-section">
              <h4><FaCode /> Code Snippet Questions</h4>
              <div className="codeSnippetResult-question-buttons">
                {questions.map((_, index) => {
                  const status = getQuestionStatus(index);
                  return (
                    <button
                      key={index}
                      className={`codeSnippetResult-question-btn codeSnippetResult-question-${status}`}
                      title={`Question ${index + 1} - ${getStatusText(status)}`}
                      onClick={() => handleQuestionClick(index)}
                    >
                      <span className="codeSnippetResult-question-number">{index + 1}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Legend Section */}
          <div className="codeSnippetResult-legend">
            <h4>Question Status</h4>
            <div className="codeSnippetResult-legend-item">
              <div className="codeSnippetResult-legend-circle codeSnippetResult-legend-correct"></div>
              <span>Correct Answered</span>
            </div>
            <div className="codeSnippetResult-legend-item">
              <div className="codeSnippetResult-legend-circle codeSnippetResult-legend-wrong"></div>
              <span>Wrong Answered</span>
            </div>
            <div className="codeSnippetResult-legend-item">
              <div className="codeSnippetResult-legend-circle codeSnippetResult-legend-unanswered"></div>
              <span>Unanswered</span>
            </div>
          </div>
        </div>

        {/* Results Content */}
        <div className="codeSnippetResult-main-content">
          <div className="codeSnippetResult-results-section">
            {/* Score Card */}
            <div className="codeSnippetResult-score-card">
              <div className="score-header">
                <FaTrophy className="trophy-icon" />
                <h3>Your Score</h3>
              </div>
              <div className="score-display">
                <div className="score-percentage">{score?.percentage || 0}%</div>
                <div className="score-details">
                  <div className="score-item">
                    <FaCheckCircle className="score-icon correct" />
                    <span>Correct: {score?.correct || 0} / {score?.total || 0}</span>
                  </div>
                  <div className="score-item">
                    <FaChartBar className="score-icon" />
                    <span>Marks: {score?.obtainedMarks || 0} / {score?.totalMarks || 0}</span>
                  </div>
                  <div className="score-item">
                    <FaClock className="score-icon" />
                    <span>Time: {score?.timeTaken || '00:00'}</span>
                  </div>
                </div>
              </div>
              {resultSaved && (
                <div className="result-saved">
                  <FaCheckCircle className="saved-icon" />
                  <span>Result saved successfully!</span>
                </div>
              )}
            </div>

            {/* Questions Review */}
            <div className="codeSnippetResult-questions-review">
              {(() => {
                const question = questions[selectedQuestionIndex];
                const status = getQuestionStatus(selectedQuestionIndex);
                const userAnswer = currentAnswers[selectedQuestionIndex];
                
                return (
                  <div className="codeSnippetResult-question-review">
                    <div className="question-review-header">
                      <h4>Question {selectedQuestionIndex + 1}: {question?.title || `Question ${selectedQuestionIndex + 1}`}</h4>
                      <div className={`result-indicator ${status}`}>
                        {getStatusIcon(status)}
                        <span>{getStatusText(status)}</span>
                      </div>
                    </div>
                    
                    <div className="question-review-content">
                      <div className="question-description">
                        <h5>Question:</h5>
                        <p>{question?.description || score?.answers?.[selectedQuestionIndex]?.explanation || "No description available"}</p>
                      </div>
                      
                      {question?.codeSnippet && (
                        <div className="code-section">
                          <h5>Code Snippet:</h5>
                          <div className="code-display">
                            <pre>{question.codeSnippet}</pre>
                          </div>
                        </div>
                      )}

                      <div className="answer-section">
                        <h5>Your Answer:</h5>
                        <div className={`selected-answer ${status}`}>
                          {userAnswer || "Not answered"}
                        </div>
                      </div>

                      <div className="correct-answer-section">
                        <h5>Correct Answer:</h5>
                        <div className="correct-answer">
                          {question?.correctAnswer || score?.answers?.[selectedQuestionIndex]?.correct || "Not available"}
                        </div>
                      </div>

                      <div className="explanation-section">
                        <h5>Explanation:</h5>
                        <div className="explanation">
                          {question?.explanation || score?.answers?.[selectedQuestionIndex]?.explanation || "No explanation available"}
                        </div>
                      </div>

                      <div className="marks-section">
                        <h5>Marks:</h5>
                        <div className="marks-display">
                          <span className="marks-obtained">
                            {status === 'correct' ? (question?.marks || 1) : 0}
                          </span>
                          <span className="marks-separator">/</span>
                          <span className="marks-total">{question?.marks || 1}</span>
                        </div>
                      </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="codeSnippetResult-navigation">
                      <button 
                        className="codeSnippetResult-nav-btn"
                        onClick={() => setSelectedQuestionIndex(Math.max(0, selectedQuestionIndex - 1))}
                        disabled={selectedQuestionIndex === 0}
                      >
                        Previous Question
                      </button>
                      <span className="codeSnippetResult-question-counter">
                        {selectedQuestionIndex + 1} of {questions.length}
                      </span>
                      <button 
                        className="codeSnippetResult-nav-btn"
                        onClick={() => setSelectedQuestionIndex(Math.min(questions.length - 1, selectedQuestionIndex + 1))}
                        disabled={selectedQuestionIndex === questions.length - 1}
                      >
                        Next Question
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CodeSnippetResult; 